import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import List from 'react-virtualized/dist/commonjs/List';
import defaultCellRangeRenderer from 'react-virtualized/dist/commonjs/Grid/defaultCellRangeRenderer';
import CellMeasurer, { CellMeasurerCache } from 'react-virtualized/dist/commonjs/CellMeasurer';
import { H3 } from '@adsk/bim360-matrix-react-h';

import './_StickyHeaderList.scss';

import getDimensions from '@adsk/bim360-matrix-react-helpers/es/getDimensions';

export default class StickyHeaderList extends React.Component {
  constructor(props) {
    super(props);

    this.groupKeyByGroupIndex = [];
    this.totalItems = this.getTotalItems(props);
    this.listContainerProps = {
      'data-testid': 'StickyHeaderList'
    };
    this._itemsCellCache = new CellMeasurerCache({
      defaultHeight: 35,
      keyMapper: rowIndex => {
        const item = this.totalItems[rowIndex];

        return item.isHeader || item.isPrefix
          ? this.props.getId({ group: item.original })
          : this.props.getId({
            group: item.group.original,
            item: item.original,
            itemIndex: item.itemIndex
          });
      }
    });

    this.state = {
      totalItems: this.totalItems,
      visibleGroupTops: new Map(),
      containerStyle: { height: props.maxHeight },
      dimensions: this.getDimensions(props)
    };

    this.setListRef = this.setListRef.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.cellRangeRenderer = this.cellRangeRenderer.bind(this);
    this.renderVisibleGroupHeaders = this.renderVisibleGroupHeaders.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.groups !== nextProps.groups ||
      nextProps.prefixGroup !== this.props.prefixGroup
    ) {
      const totalItems = this.getTotalItems(nextProps);
      this.totalItems = totalItems;
      this.rowRenderer = this.rowRenderer.bind(this);
      this._itemsCellCache.clearAll();
      this.setState({ totalItems });
    }

    if (
      nextProps.itemRenderer !== this.props.itemRenderer ||
      nextProps.prefixItemRenderer !== this.props.prefixItemRenderer
    ) {
      this.rowRenderer = this.rowRenderer.bind(this);
    }

    if (
      nextProps.maxHeight !== this.props.maxHeight ||
      nextProps.maxWidth !== this.props.maxWidth
    ) {
      this.setContainerStyle(nextProps);
    }
  }

  componentDidMount() {
    this.mounted = true;
    // This is a hack because for some reason the initial mount doesn't calcualte the children dimensions correctly.
    // If calculating in the next frame (while clearing the previous cached values) we'll measure the children correctly.
    requestAnimationFrame(() => {
      if (this.mounted) {
        this.setContainerStyle(this.props);
        this._itemsCellCache.clearAll();
        this.list.recomputeRowHeights();
      }
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps, prevState) {
    // All of these things affect the height/width of the container.
    // When one of them changes we need to resize the container & all of the child elements.
    if (
      this.state.totalItems !== prevState.totalItems ||
      prevProps.itemRenderer !== this.props.itemRenderer ||
      prevProps.prefixItemRenderer !== this.props.prefixItemRenderer ||
      prevProps.maxHeight !== this.props.maxHeight ||
      prevProps.maxWidth !== this.props.maxWidth
    ) {
      this.setContainerStyle(this.props);
      this.list.recomputeRowHeights();
    }
  }

  setContainerStyle(props) {
    const dimensions = this.getDimensions(props);
    this.setState({ containerStyle: { height: dimensions.height }, dimensions });
  }

  getDimensions(props) {
    return getDimensions({
      items: this.totalItems,
      getItemDimensions: index => ({
        height: this._itemsCellCache.getHeight(index),
        width: this._itemsCellCache.getWidth(index)
      }),
      maxHeight: props.maxHeight,
      maxWidth: props.maxWidth
    });
  }

  getTotalItems(props = this.props) {
    this.groupKeyByGroupIndex = [];
    const generateGroupItems = (group, originalGroup, previousItemCount) =>
      originalGroup.items.map((original, itemIndex) => ({
        itemIndex,
        group,
        original,
        index: itemIndex + previousItemCount
      }));
    const createGroup = customData => original => {
      const group = { original, ...customData };
      group.group = group;
      return group;
    };

    const prefixGroup = createGroup({ isPrefix: true })(props.prefixGroup);
    const seed = prefixGroup.original && prefixGroup.original.items.length ? [prefixGroup] : [];

    const groups = seed.concat(
      props.groups.filter(group => group.items.length > 0).map(createGroup({ isHeader: true }))
    );
    return groups.reduce((items, group) => {
      group.index = items.length;
      return items
        .concat([group])
        .concat(generateGroupItems(group, group.original, items.length + 1));
    }, []);
  }

  renderHeader({ style, group, ...rest }) {
    const customProps = (group.getHeaderProps || (() => ({})))({ group });
    return (
      <H3
        {...rest}
        data-testid={`StickyHeaderList__group-header__${group.header}`}
        {...customProps}
        className="StickyHeaderList__group-header"
        style={style}
      >
        {group.header}
      </H3>
    );
  }

  renderCellMeasurer({ index, key, parent, children }) {
    return (
      <CellMeasurer
        key={key}
        parent={parent}
        rowIndex={index}
        columnIndex={0}
        cache={this._itemsCellCache}
      >
        {children}
      </CellMeasurer>
    );
  }

  renderGroupHeader({ style, group, ...cellMeasuererProps }) {
    this.groupKeyByGroupIndex[cellMeasuererProps.index] = cellMeasuererProps.key;

    return this.renderCellMeasurer({
      ...cellMeasuererProps,
      children: this.renderHeader({ style: { ...style, color: 'transparent' }, group })
    });
  }

  renderItem({ style, group, item, itemRenderer, ...cellMeasuererProps }) {
    itemRenderer = itemRenderer || this.props.itemRenderer;
    return this.renderCellMeasurer({
      ...cellMeasuererProps,
      children: itemRenderer({ index: cellMeasuererProps.index, item, group, style })
    });
  }

  renderEmpty({ ...cellMeasuererProps }) {
    return this.renderCellMeasurer({
      ...cellMeasuererProps,
      children: <div style={{ height: 0 }} />
    });
  }

  rowRenderer(cellMeasuererProps) {
    const item = this.state.totalItems[cellMeasuererProps.index];
    if (item.isPrefix) {
      return this.renderEmpty(cellMeasuererProps);
    } else if (item.group.isPrefix) {
      return this.renderItem({
        ...cellMeasuererProps,
        group: item.group.original,
        item: item.original,
        itemRenderer: this.props.prefixItemRenderer
      });
    } else if (item.isHeader) {
      return this.renderGroupHeader({ ...cellMeasuererProps, group: item.original });
    } else {
      return this.renderItem({
        ...cellMeasuererProps,
        group: item.group.original,
        item: item.original
      });
    }
  }

  cellRangeRenderer(originalProps) {
    const children = defaultCellRangeRenderer(originalProps);
    const visibleGroupTops = new Map();
    const visibleGroupIndicies = new Map();
    const startingIndex = originalProps.visibleRowIndices.start;
    const endingIndex = Math.min(
      originalProps.visibleRowIndices.stop + 1,
      this.state.totalItems.length
    );

    // In case we still don't have any visible range to render, we don't have anything to use at this point.
    if (startingIndex === undefined || originalProps.visibleRowIndices.stop === undefined) {
      return children;
    }

    let counter = 0;

    for (let rowIndex = startingIndex; rowIndex < endingIndex; rowIndex++) {
      const group = this.state.totalItems[rowIndex].group;
      if (!visibleGroupTops.has(group)) {
        const groupStyleCache = originalProps.styleCache[this.groupKeyByGroupIndex[group.index]];

        // If the current group has a style cache we need to use it.
        // If there's no style cache we're gonna default to 0 and the next render will fix it.
        if (groupStyleCache) {
          visibleGroupTops.set(group, groupStyleCache.top - originalProps.scrollTop);
        } else {
          visibleGroupTops.set(group, 0);
        }

        visibleGroupIndicies.set(counter, group);
        counter++;
      }
    }

    // Now we're getting the correct values of the tops of all the visible groups.
    // Adjusting the top of the first group to support for the "sliding out" effect.
    const firstGroup = visibleGroupIndicies.get(0);
    const firstGroupHeaderHeight = this._itemsCellCache.getHeight(firstGroup.index);
    const secondGroupTop =
      visibleGroupTops.get(visibleGroupIndicies.get(1)) || firstGroupHeaderHeight;

    visibleGroupTops.set(firstGroup, Math.min(0, secondGroupTop - firstGroupHeaderHeight));

    const areMapsEqual = (map1, map2) => {
      if (map1.size !== map2.size) return false;

      let result = true;
      map1.forEach((val, key) => {
        if (result && (!map2.has(key) || map2.get(key) !== val)) {
          result = false;
        }
      });

      return result;
    };

    if (!areMapsEqual(this.state.visibleGroupTops, visibleGroupTops)) {
      this.setState({ visibleGroupTops });
    }

    return children;
  }

  renderVisibleGroupHeaders() {
    const headers = [];

    this.state.visibleGroupTops.forEach((value, key) => {
      const group = key;
      // Only if the group isn't a prefix one it means we have a header to show - hence we need to make it sticky-able.
      if (!group.isPrefix) {
        const floatingHeaderStyle = {
          top: `${value}px`,
          position: 'absolute',
          zIndex: 1,
          width: 'calc(100% - 15px)',
          pointerEvents: 'none'
        };

        headers.push(
          this.renderHeader({ style: floatingHeaderStyle, group: group.original, key: group.index })
        );
      }
    });

    return headers;
  }

  listRef() {
    return this.list;
  }

  setListRef(list) {
    this.list = list;
  }

  render() {
    return (
      <div className={classNames(this.props.className, 'StickyHeaderList')}>
        <div className="StickyHeaderList__virtual-list-container" style={this.state.containerStyle}>
          <List
            ref={this.setListRef}
            className="StickyHeaderList__virtual-list"
            cellRangeRenderer={this.cellRangeRenderer}
            containerProps={this.listContainerProps}
            height={this.state.dimensions.height}
            rowCount={this.state.totalItems.length}
            deferredMeasurementCache={this._itemsCellCache}
            rowHeight={this._itemsCellCache.rowHeight}
            rowRenderer={this.rowRenderer}
            overscanRowCount={20}
            width={this.state.dimensions.width}
          />
          {this.renderVisibleGroupHeaders()}
        </div>
        {this.props.renderFooter({
          getContainerProps: ({ className, ...rest }) => ({
            className: classNames('StickyHeaderList__footer', className),
            ...rest
          })
        })}
      </div>
    );
  }
}

StickyHeaderList.defaultProps = {
  maxHeight: 320,
  renderFooter: noop,
  getId: ({ group, item }) => (item ? item.id : group.id)
};

StickyHeaderList.propTypes = {
  /**
   * component class name
   */
  className: PropTypes.string,
  /**
   * A render function to render the footer of the list.
   * The function is of the form:
   * `({ getContainerProps: ({ className, ...rest }) => Object }) => React.node`
   *
   * `getContainerProps` - Used to get all the props of the footer container, gets a className if one is needed to be added.
   *  The `rest` of the props passed to `getContainerProps` are being transferred as-is to the container.
   */
  renderFooter: PropTypes.func,
  /**
   * An option list to be displayed before the sticky list
   */
  prefixGroup: PropTypes.shape({
    /**
     * An array of items that belong to the prefix group.
     */
    items: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.shape({
          /**
           * The id of the item, should be unique among all items in all groups and the groups themselves.
           */
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }),
        PropTypes.any
      ])
    ).isRequired,
    /**
     * The id of the prefix group, should be unique among all items, not just groups.
     */
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /**
   * A function that returns the unique ID of each element, that includes groups and items alike.
   * This is an advanced usage prop, in case you don't have `id` for your groups and items.
   * The function is of the form:
   * ({ group: Object, item?: Object }) => String|Number
   *
   * If you only get a group object => the returned id should be for the given group.
   * If you get a group and an item => the returned id should be for the given item in the specific group.
   */
  getId: PropTypes.func,
  /**
   * An array consists of group tuples (groupLabel and options component)
   */
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * The header of the group.
       */
      header: PropTypes.string.isRequired,
      /**
       * An array of items that belong to this specific group.
       */
      items: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.shape({
            /**
             * The id of the item, should be unique among all items in all groups and the groups themselves.
             */
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          }),
          PropTypes.any
        ])
      ).isRequired,
      /**
       * The id of the group, should be unique among all items in all groups and the groups themselves.
       */
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      /**
       * Function that returns custom props to apply on the header element.
       * The function is of the form:
       * `({ group: Object }) => Object`
       */
      getHeaderProps: PropTypes.func
    })
  ).isRequired,
  /**
   * How to render each item in the prefix group.
   * The function has the following signature:
   * `({ index: Number, itemIndex: Number, group: Object, style: Object }) => React.Node`
   *
   * The `group` object is the original group object, any data attached to it will be also accessible here.
   * The difference between `index` and `itemIndex` is the fact that `index` refers to index among all items in the list (including group headers),
   * while `itemIndex` is the index of the item within the current group.
   *
   * If not specificed it'll default to props.itemRenderer
   */
  prefixItemRenderer: PropTypes.func,
  /**
   * How to render each item in the group.
   * The function has the following signature:
   * `({ index: Number, itemIndex: Number, group: Object, style: Object }) => React.Node`
   *
   * The `group` object is the original group object, any data attached to it will be also accessible here.
   * The difference between `index` and `itemIndex` is the fact that `index` refers to index among all items in the list (including group headers),
   * while `itemIndex` is the index of the item within the current group.
   */
  itemRenderer: PropTypes.func.isRequired,
  /**
   * Max height for the container, used for virtualization calculations.
   */
  maxHeight: PropTypes.number,
  /**
   * The max width (in pixels) for the entire list, if the content exceeds this number it'll be trimmed.
   */
  maxWidth: PropTypes.number
};
